<template>
    <main class="grouping-detail">
        <header class="grouping-detail__header">
            <button type="button" class="icon-button" title="Voltar" @click="back"><ArrowLeftIcon /></button>
            <div v-if="grouping" class="grouping-detail__identity">
                <div>
                    <h1>{{ grouping.code }}</h1>
                    <span :class="['type-pill', grouping.type === 'NESTING' ? 'type-pill--nesting' : '']">
                        {{ typeLabel(grouping.type) }}
                    </span>
                </div>
                <p>{{ grouping.description }}</p>
            </div>
            <div v-if="grouping" class="grouping-detail__actions">
                <button
                    type="button"
                    class="secondary-button"
                    :disabled="busy"
                    @click="history"
                ><ClockIcon /> Histórico</button>
                <button
                    v-if="isWaiting"
                    type="button"
                    class="secondary-button"
                    :disabled="busy"
                    @click="edit"
                ><Edit2Icon /> Editar</button>
                <button
                    v-if="grouping.canDelete"
                    type="button"
                    class="danger-button"
                    :disabled="busy"
                    @click="deleteGrouping"
                ><Trash2Icon /> Excluir</button>
                <button
                    v-if="!isClosed"
                    type="button"
                    class="danger-button"
                    :disabled="busy"
                    @click="cancelGrouping"
                ><XCircleIcon /> Cancelar</button>
            </div>
        </header>

        <div v-if="loading" class="state-message"><LoaderIcon class="spin" /> Carregando agrupamento</div>
        <div v-else-if="error" class="state-message state-message--error">
            <AlertCircleIcon /> {{ error }}
            <button type="button" @click="load">Tentar novamente</button>
        </div>

        <template v-else-if="grouping">
            <section class="metrics-band">
                <div><small>Tipo</small><strong>{{ typeLabel(grouping.type) }}</strong></div>
                <div><small>Recurso</small><strong>{{ grouping.resourceName }}</strong></div>
                <div><small>Ordens vinculadas</small><strong>{{ grouping.orders.length }}</strong></div>
                <div><small>Total planejado</small><strong>{{ formatNumber(grouping.totalPlanned) }}</strong></div>
                <div><small>Total produzido</small><strong>{{ formatNumber(grouping.totalProduced) }}</strong></div>
                <div><small>Refugo</small><strong>{{ formatNumber(grouping.totalRefuse) }}</strong></div>
            </section>

            <OrderGroupingTimers class="timers-band" :grouping="grouping" />

            <section v-if="!isClosed" class="workflow-action">
                <div class="workflow-action__copy">
                    <span class="workflow-action__icon"><ActivityIcon /></span>
                    <div>
                        <h2>{{ operationalTitle }}</h2>
                        <p>{{ operationalDescription }}</p>
                    </div>
                </div>
                <div class="workflow-action__buttons">
                    <button v-if="canSetup" type="button" class="secondary-button" :disabled="busy" @click="setup">
                        <ToolIcon /> Setup
                    </button>
                    <button v-if="canStart" type="button" class="primary-button" :disabled="busy" @click="start">
                        <PlayCircleIcon /> {{ isStopped ? "Continuar" : "Iniciar" }}
                    </button>
                    <button v-if="isRunning" type="button" class="primary-button" :disabled="busy" @click="appointment">
                        <ClipboardIcon /> Apontar
                    </button>
                    <button v-if="canStop" type="button" class="secondary-button" :disabled="busy" @click="stop">
                        <PauseCircleIcon /> Parada
                    </button>
                    <button v-if="canFinish" type="button" class="finish-button" :disabled="busy" @click="finish">
                        <CheckCircleIcon /> Finalizar
                    </button>
                </div>
            </section>

            <section class="orders-panel">
                <header>
                    <div>
                        <h2>Composição do agrupamento</h2>
                        <p>Ordens de produção vinculadas ao recurso {{ grouping.resourceName }}.</p>
                    </div>
                    <span>{{ grouping.orders.length }} ordens</span>
                </header>
                <div class="table-scroll">
                    <table>
                        <thead>
                            <tr>
                                <th>Seq.</th>
                                <th>Ordem</th>
                                <th>Operação</th>
                                <th>Item</th>
                                <th>Descrição</th>
                                <th class="numeric">Planejado</th>
                                <th class="numeric">Produzido</th>
                                <th class="numeric">Retrabalho</th>
                                <th class="numeric">Refugo</th>
                                <th class="numeric">Rateio</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="order in grouping.orders" :key="order.id">
                                <td>{{ order.sequence }}</td>
                                <td><strong>{{ order.orderNumber }}</strong></td>
                                <td>{{ order.operationNumber }} - {{ order.operationName }}</td>
                                <td>{{ order.itemCode }}</td>
                                <td class="description-cell">{{ order.description }}</td>
                                <td class="numeric">{{ formatNumber(order.plannedQuantity) }}</td>
                                <td class="numeric">{{ formatNumber(order.producedQuantity) }}</td>
                                <td class="numeric">{{ formatNumber(order.reworkQuantity) }}</td>
                                <td class="numeric">{{ formatNumber(order.refuseQuantity) }}</td>
                                <td class="numeric">{{ allocationValue(order) }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </section>

        </template>

        <OrderGroupingActionModal
            :open="stopModalOpen"
            title="Registrar parada"
            eyebrow="Parada operacional"
            description="O tempo de execução será pausado e o motivo ficará registrado na trilha do agrupamento."
            tone="warning"
            confirm-label="Confirmar parada"
            cancel-label="Voltar"
            :busy="busy"
            :error="error"
            :confirm-disabled="!selectedStopStatusId"
            @cancel="closeStopModal"
            @confirm="confirmStop"
        >
            <PauseCircleIcon slot="icon" />
            <div class="modal-context">
                <span>Agrupamento</span>
                <strong>{{ grouping ? grouping.code : "-" }}</strong>
                <small>{{ grouping ? grouping.currentStatusName : "" }}</small>
            </div>
            <label class="modal-field" for="grouping-stop-reason">
                <span>Motivo da parada <b>*</b></span>
                <select id="grouping-stop-reason" v-model.number="selectedStopStatusId">
                    <option :value="null" disabled>Selecione o motivo</option>
                    <option v-for="status in stopReasons" :key="status.id" :value="Number(status.id)">
                        {{ status.name }}
                    </option>
                </select>
            </label>
        </OrderGroupingActionModal>

        <OrderGroupingActionModal
            :open="finishModalOpen"
            :title="finishIsPartial ? 'Finalizar antes do previsto?' : 'Finalizar agrupamento?'"
            :eyebrow="finishIsPartial ? 'Produção parcial' : 'Encerramento'"
            :description="finishDescription"
            :tone="finishIsPartial ? 'warning' : 'success'"
            confirm-label="Finalizar agrupamento"
            :cancel-label="finishIsPartial ? 'Continuar produção' : 'Voltar'"
            :busy="busy"
            :error="error"
            @cancel="closeFinishModal"
            @confirm="confirmFinish"
        >
            <AlertTriangleIcon v-if="finishIsPartial" slot="icon" />
            <CheckCircleIcon v-else slot="icon" />
            <div class="finish-summary">
                <div><small>Previsto</small><strong>{{ formatNumber(grouping ? grouping.totalPlanned : 0) }}</strong></div>
                <div><small>Produzido</small><strong>{{ formatNumber(grouping ? grouping.totalProduced : 0) }}</strong></div>
                <div><small>Saldo</small><strong>{{ formatNumber(finishBalance) }}</strong></div>
            </div>
            <div v-if="finishIsPartial" class="partial-warning">
                <AlertTriangleIcon />
                <span>Depois de finalizado, este agrupamento não aceitará novos apontamentos.</span>
            </div>
        </OrderGroupingActionModal>
    </main>
</template>

<script>
import {
    ActivityIcon,
    AlertCircleIcon,
    AlertTriangleIcon,
    ArrowLeftIcon,
    CheckCircleIcon,
    ClockIcon,
    ClipboardIcon,
    Edit2Icon,
    LoaderIcon,
    PauseCircleIcon,
    PlayCircleIcon,
    ToolIcon,
    Trash2Icon,
    XCircleIcon
} from "vue-feather-icons";
import ToastificationContent from "@/@core/components/toastification/ToastificationContent.vue";
import createOrderGroupingApi from "./orderGroupingApi";
import OrderGroupingActionModal from "./OrderGroupingActionModal.vue";
import OrderGroupingTimers from "./OrderGroupingTimers.vue";
import {
    allocationLabel,
    apiErrorMessage,
    statusLabel,
    typeLabel
} from "./groupingPresentation";

export default {
    components: {
        ActivityIcon,
        AlertCircleIcon,
        AlertTriangleIcon,
        ArrowLeftIcon,
        CheckCircleIcon,
        ClockIcon,
        ClipboardIcon,
        Edit2Icon,
        LoaderIcon,
        OrderGroupingActionModal,
        OrderGroupingTimers,
        PauseCircleIcon,
        PlayCircleIcon,
        ToolIcon,
        Trash2Icon,
        XCircleIcon
    },
    data() {
        return {
            grouping: null,
            statuses: [],
            loading: false,
            busy: false,
            error: "",
            stopModalOpen: false,
            finishModalOpen: false,
            selectedStopStatusId: null,
            finishIsPartial: false
        };
    },
    computed: {
        isClosed() {
            return ["FINISHED", "CANCELLED"].includes(this.grouping?.status);
        },
        isWaiting() {
            return this.grouping?.status === "WAITING";
        },
        currentStatusName() {
            return String(this.grouping?.currentStatusName || "").toLocaleLowerCase("pt-BR");
        },
        isSetup() {
            return this.currentStatusName === "setup";
        },
        isRunning() {
            return ["executando", "running"].includes(this.currentStatusName);
        },
        isStopped() {
            return !this.isClosed && [2, 3].includes(Number(this.grouping?.currentStatusClassificationId));
        },
        canSetup() {
            return this.isWaiting || this.isStopped;
        },
        canStart() {
            return this.isWaiting || this.isSetup || this.isStopped;
        },
        canStop() {
            return this.isSetup || this.isRunning;
        },
        canFinish() {
            return !this.isWaiting && !this.isClosed;
        },
        stopReasons() {
            return this.statuses.filter(status =>
                [2, 3].includes(Number(status.status_classification_id ?? status.statusClassificationId))
            );
        },
        finishBalance() {
            if (!this.grouping) return 0;
            if (this.grouping.type === "NESTING")
                return Math.max(Number(this.grouping.totalPlanned) - Number(this.grouping.totalProduced), 0);
            return this.grouping.orders.reduce((total, order) => total + Math.max(
                Number(order.plannedQuantity) - Number(order.producedQuantity),
                0
            ), 0);
        },
        finishDescription() {
            return this.finishIsPartial
                ? "A quantidade produzida ainda é menor que a prevista. Confirme se deseja encerrar mesmo assim."
                : "Confirme o encerramento. O tempo do agrupamento será congelado e as operações elegíveis serão finalizadas.";
        },
        operationalTitle() {
            if (this.isWaiting) return "Pronto para iniciar";
            if (this.isSetup) return "Setup em andamento";
            if (this.isRunning) return "Agrupamento em execução";
            if (this.isStopped) return this.grouping.currentStatusName;
            return this.grouping.currentStatusName || statusLabel(this.grouping.status);
        },
        operationalDescription() {
            if (this.isWaiting) return "Inicie diretamente ou registre o tempo de Setup antes da execução.";
            if (this.isSetup) return "Conclua o Setup iniciando a execução ou registre uma parada.";
            if (this.isRunning && this.grouping.type === "NESTING")
                return "Informe a produção total do conjunto e o refugo individual de cada OP.";
            if (this.isRunning)
                return "Informe produzido, retrabalho e refugo individualmente para cada OP.";
            if (this.isStopped) return "Continue a execução ou retorne ao Setup antes de finalizar.";
            return "";
        }
    },
    created() {
        this.load();
    },
    methods: {
        statusLabel,
        typeLabel,
        async load() {
            this.loading = true;
            this.error = "";
            try {
                const api = createOrderGroupingApi(this.$http2);
                this.grouping = await api.get(this.$route.params.id);
                try {
                    const context = await api.appointmentContext(this.grouping.siteId);
                    this.statuses = context.status || [];
                } catch (_) {
                    this.statuses = [];
                }
            } catch (error) {
                this.error = apiErrorMessage(error);
            } finally {
                this.loading = false;
            }
        },
        formatNumber(value) {
            return Number(value || 0).toLocaleString("pt-BR", { maximumFractionDigits: 4 });
        },
        allocationValue(order) {
            const allocation = this.grouping.allocationType;
            if (["PERCENTAGE"].includes(allocation))
                return `${this.formatNumber(order.allocationPercentage)}%`;
            if (["FIXEDQUANTITY", "FIXED_QUANTITY", "PLANNEDQUANTITY", "PLANNED_QUANTITY"].includes(allocation))
                return this.formatNumber(order.allocatedQuantity);
            return allocationLabel(allocation);
        },
        back() {
            this.$router.push({
                name: "operator-order-groupings",
                params: { prefix: this.$route.params.prefix }
            });
        },
        edit() {
            this.$router.push({
                name: "operator-order-grouping-edit",
                params: { prefix: this.$route.params.prefix, id: this.grouping.id }
            });
        },
        appointment() {
            this.$router.push({
                name: "operator-order-grouping-appointment",
                params: { prefix: this.$route.params.prefix, id: this.grouping.id }
            });
        },
        history() {
            this.$router.push({
                name: "operator-order-grouping-history",
                params: { prefix: this.$route.params.prefix, id: this.grouping.id }
            });
        },
        async runAction(action, payload = { eventTime: new Date().toISOString() }) {
            if (this.busy) return;
            this.busy = true;
            this.error = "";
            try {
                await createOrderGroupingApi(this.$http2)[action](this.grouping.id, payload);
                await this.load();
            } catch (error) {
                this.error = apiErrorMessage(error);
                if (action === "finish" && this.error.includes("quantidade faltando")) {
                    this.$toast({ component: ToastificationContent, props: {
                        title: this.$t("ProductionProgress.FinishBelowPlannedBlocked"),
                        icon: "AlertTriangleIcon", variant: "warning"
                    } });
                }
            } finally {
                this.busy = false;
            }
        },
        setup() {
            return this.runAction("setup");
        },
        start() {
            return this.runAction("start");
        },
        stop() {
            if (!this.stopReasons.length) {
                this.error = "Nenhum motivo de parada está configurado para este site.";
                return;
            }
            this.error = "";
            this.selectedStopStatusId = null;
            this.stopModalOpen = true;
        },
        closeStopModal() {
            this.stopModalOpen = false;
            this.selectedStopStatusId = null;
        },
        async confirmStop() {
            const statusId = Number(this.selectedStopStatusId);
            if (!statusId) return;
            await this.runAction("stop", { statusId, eventTime: new Date().toISOString() });
            if (!this.error) this.closeStopModal();
        },
        isProductionComplete() {
            if (this.grouping.type === "NESTING")
                return Number(this.grouping.totalProduced) >= Number(this.grouping.totalPlanned);
            return this.grouping.orders.every(order =>
                Number(order.producedQuantity) >= Number(order.plannedQuantity)
            );
        },
        finish() {
            this.error = "";
            this.finishIsPartial = !this.isProductionComplete();
            this.finishModalOpen = true;
        },
        closeFinishModal() {
            this.finishModalOpen = false;
        },
        async confirmFinish() {
            await this.runAction("finish", {
                confirmPartial: this.finishIsPartial,
                eventTime: new Date().toISOString()
            });
            if (!this.error) this.closeFinishModal();
        },
        async deleteGrouping() {
            const confirmation = await this.$swal({
                title: "Excluir agrupamento?",
                text: "Esta ação remove o agrupamento e sua composição.",
                icon: "warning",
                showCancelButton: true,
                confirmButtonText: "Excluir",
                cancelButtonText: "Voltar"
            });
            if (!confirmation.isConfirmed && !confirmation.value) return;

            this.busy = true;
            try {
                await createOrderGroupingApi(this.$http2).delete(this.grouping.id);
                this.back();
            } catch (error) {
                this.error = apiErrorMessage(error);
            } finally {
                this.busy = false;
            }
        },
        async cancelGrouping() {
            const confirmation = await this.$swal({
                title: "Cancelar agrupamento?",
                text: "O agrupamento continuará disponível para consulta, mas não aceitará novos apontamentos.",
                icon: "warning",
                showCancelButton: true,
                confirmButtonText: "Cancelar agrupamento",
                cancelButtonText: "Voltar"
            });
            if (!confirmation.isConfirmed && !confirmation.value) return;

            this.busy = true;
            try {
                await createOrderGroupingApi(this.$http2).cancel(this.grouping.id);
                await this.load();
            } catch (error) {
                this.error = apiErrorMessage(error);
            } finally {
                this.busy = false;
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.grouping-detail {
    width: 100%;
    color: #4c4541;

    &__header {
        display: flex;
        align-items: center;
        gap: 14px;
        margin-bottom: 16px;
    }

    &__identity {
        flex: 1;
        min-width: 0;

        > div {
            display: flex;
            align-items: center;
            gap: 9px;
        }

        h1 {
            margin: 0;
            font-family: monospace;
            font-size: 22px;
            font-weight: 700;
        }

        p {
            margin: 3px 0 0;
            overflow: hidden;
            color: #998f8a;
            text-overflow: ellipsis;
            white-space: nowrap;
        }
    }

    &__actions {
        display: flex;
        gap: 8px;
    }
}

.icon-button,
.secondary-button,
.danger-button,
.primary-button,
.finish-button {
    min-height: 40px;
    border-radius: 5px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
}

.icon-button {
    width: 40px;
    border: 1px solid #cfc4be;
    background: #fff;
    color: #974900;
}

.secondary-button,
.danger-button {
    padding: 0 13px;
    background: #fff;
    font-weight: 600;
}

.secondary-button { border: 1px solid #974900; color: #974900; }
.danger-button { border: 1px solid #c33a2c; color: #b42318; }
.primary-button { border: 1px solid #974900; background: #974900; color: #fff; padding: 0 16px; font-weight: 600; }
.finish-button { border: 1px solid #237a3b; background: #237a3b; color: #fff; padding: 0 16px; font-weight: 600; }
button:disabled { opacity: 0.45; }

.type-pill,
.status-pill {
    display: inline-flex;
    align-items: center;
    min-height: 22px;
    border-radius: 11px;
    padding: 2px 9px;
    font-size: 11px;
    font-weight: 600;
    white-space: nowrap;
}

.type-pill { background: #edf5ff; color: #2463a5; }
.type-pill--nesting { background: #f4edff; color: #6f42a5; }
.status--waiting { background: #fff5dc; color: #835900; }
.status--progress { background: #e7f2ff; color: #1760a5; }
.status--finished { background: #e8f7ed; color: #237a3b; }
.status--cancelled { background: #f3f1f0; color: #7e7570; }

.metrics-band {
    display: grid;
    grid-template-columns: repeat(6, minmax(100px, 1fr));
    border: 1px solid #d8d6de;
    border-radius: 6px;
    background: #fff;
    overflow: hidden;

    > div {
        min-height: 78px;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 10px;
        text-align: center;

        + div {
            border-left: 1px solid #ece0db;
        }
    }

    small {
        margin-bottom: 5px;
        color: #998f8a;
    }
}

.timers-band { margin-top: 14px; }

.workflow-action {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 18px;
    margin-top: 14px;
    padding: 14px 16px;
    border: 1px solid #d8d6de;
    border-left: 4px solid #f3811a;
    border-radius: 6px;
    background: #fff;

    &__copy,
    &__buttons {
        display: flex;
        align-items: center;
        gap: 9px;
    }

    &__copy { min-width: 0; }
    &__buttons { flex-wrap: wrap; justify-content: flex-end; }

    &__icon {
        width: 42px;
        height: 42px;
        flex: 0 0 auto;
        display: grid;
        place-items: center;
        border-radius: 5px;
        background: #fff1e8;
        color: #974900;
    }

    h2 { margin: 0; font-family: "Raleway"; font-size: 15px; font-weight: 600; }
    p { margin: 3px 0 0; color: #7e7570; font-size: 12px; }
}

.workflow-action__buttons button {
    min-height: 54px;
    padding: 0 22px;
    font-size: 14px;

    svg {
        width: 20px;
        height: 20px;
    }
}

.orders-panel {
    margin-top: 14px;
    border: 1px solid #d8d6de;
    border-radius: 6px;
    background: #fff;
    overflow: hidden;

    > header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        padding: 16px;
        border-bottom: 1px solid #ece0db;

        h2 { margin: 0; font-family: "Raleway"; font-size: 16px; font-weight: 600; }
        p { margin: 3px 0 0; color: #998f8a; font-size: 12px; }
        > span { color: #7e7570; font-size: 12px; }
    }
}

.table-scroll { overflow-x: auto; }
table { width: 100%; min-width: 1050px; border-collapse: collapse; }
th, td { padding: 11px 12px; border-bottom: 1px solid #f0ecea; font-size: 12px; text-align: left; }
th { background: #fbfaf9; color: #7e7570; font-size: 10px; text-transform: uppercase; }
.numeric { text-align: right; }
.description-cell { max-width: 250px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

.modal-context {
    display: grid;
    grid-template-columns: 1fr auto;
    align-items: center;
    gap: 3px 12px;
    margin-bottom: 18px;
    border-radius: 6px;
    padding: 12px 14px;
    background: #f7f5f4;

    span { color: #7e7570; font-size: 11px; }
    strong { grid-row: 1 / 3; grid-column: 2; font-family: monospace; font-size: 14px; }
    small { color: #998f8a; font-size: 11px; }
}

.modal-field {
    display: block;

    > span {
        display: block;
        margin-bottom: 7px;
        font-size: 12px;
        font-weight: 600;
    }

    b { color: #b42318; }

    select {
        width: 100%;
        min-height: 46px;
        border: 1px solid #cfc4be;
        border-radius: 5px;
        padding: 0 12px;
        background: #fff;
        color: #4c4541;

        &:focus {
            border-color: #974900;
            box-shadow: 0 0 0 3px rgba(151, 73, 0, 0.12);
            outline: 0;
        }
    }
}

.finish-summary {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    border: 1px solid #e2dcd8;
    border-radius: 6px;
    background: #fbfaf9;
    overflow: hidden;

    > div {
        min-height: 72px;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 10px;
        text-align: center;

        + div { border-left: 1px solid #e2dcd8; }
    }

    small { margin-bottom: 4px; color: #7e7570; font-size: 10px; text-transform: uppercase; }
    strong { font-size: 17px; }
}

.partial-warning {
    display: flex;
    align-items: flex-start;
    gap: 9px;
    margin-top: 14px;
    border-left: 3px solid #c05a00;
    border-radius: 4px;
    padding: 10px 12px;
    background: #fff5eb;
    color: #78400c;
    font-size: 12px;
    line-height: 1.4;

    svg { width: 17px; height: 17px; flex: 0 0 auto; }
}

.state-message {
    min-height: 260px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 9px;
    color: #998f8a;

    &--error { color: #b42318; }
    button { border: 0; background: transparent; color: #974900; text-decoration: underline; }
}

.spin { animation: spin 1s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }

@media (max-width: 1100px) {
    .workflow-action {
        align-items: stretch;
        flex-direction: column;
    }

    .workflow-action__buttons {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
        width: 100%;
    }

    .workflow-action__buttons button { width: 100%; }
}

@media (max-width: 960px) {
    .metrics-band {
        grid-template-columns: repeat(3, 1fr);
        > div { border-bottom: 1px solid #ece0db; }
    }
}

@media (max-width: 700px) {
    .grouping-detail__header {
        align-items: flex-start;
        flex-wrap: wrap;
    }

    .grouping-detail__identity { width: calc(100% - 54px); flex: none; }
    .grouping-detail__actions { width: 100%; button { flex: 1; } }
    .metrics-band { grid-template-columns: repeat(2, 1fr); }
    .workflow-action__buttons { display: grid; grid-template-columns: repeat(2, 1fr); }
}

@media (min-width: 576px) and (max-width: 1024px) {
    .workflow-action__buttons {
        grid-template-columns: repeat(3, minmax(0, 1fr));

        button {
            min-width: 0;
            padding-right: 12px;
            padding-left: 12px;
        }
    }
}

@media (max-width: 430px) {
    .workflow-action__buttons { grid-template-columns: 1fr; }
    .finish-summary { grid-template-columns: 1fr; }
    .finish-summary > div + div { border-top: 1px solid #e2dcd8; border-left: 0; }
}
</style>
